import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterableMultiSelect, DismissibleTag } from "@carbon/react";
import { useRoles } from "../../../hooks/useRoles";
import type { RoleRef } from "../../../types";

type RolePickerProps = {
  /** Ids of the currently assigned roles (controlled). */
  selectedIds: string[];
  /** Called with the full next list of selected role ids. */
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Visible field label; defaults to the translated "Roles". */
  labelText?: string;
  id?: string;
};

/**
 * Multi-select of the company's RBAC roles for the user create/edit forms: Carbon's
 * `FilterableMultiSelect` over `useRoles()` with the selection echoed as dismissible chips —
 * the same shape as `TagPicker`, minus the colour swatch (a role is just a name).
 *
 * Controlled-selection gotcha (same as TagPicker): Carbon matches `selectedItems` against
 * `items` by object identity, and the parent only holds ids. `items` is the fetched role list
 * merged by id with any selected id the list doesn't (yet) contain — kept as a name-less
 * placeholder `{ id, name: id }` so it still renders as checked/removable — and `selectedItems`
 * is resolved back out of that merged list from `selectedIds`.
 */
const RolePicker = ({
  selectedIds,
  onChange,
  disabled = false,
  labelText,
  id = "role-picker",
}: RolePickerProps) => {
  const { t } = useTranslation();
  const { data: roles = [] } = useRoles();

  const items = useMemo<RoleRef[]>(() => {
    const byId = new Map<string, RoleRef>();
    roles.forEach((role) =>
      byId.set(role.id, { id: role.id, name: role.name })
    );
    selectedIds.forEach((roleId) => {
      if (!byId.has(roleId)) byId.set(roleId, { id: roleId, name: roleId });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [roles, selectedIds]);

  const selectedItems = useMemo(() => {
    const wanted = new Set(selectedIds);
    return items.filter((role) => wanted.has(role.id));
  }, [items, selectedIds]);

  const handleRemove = (roleId: string) =>
    onChange(selectedIds.filter((existing) => existing !== roleId));

  return (
    <div className="role-picker">
      <FilterableMultiSelect
        id={id}
        size="lg"
        titleText={labelText ?? t("roles.title")}
        placeholder={t("cta.select")}
        items={items}
        itemToString={(role) => role?.name ?? ""}
        selectedItems={selectedItems}
        onChange={({ selectedItems: next }) =>
          onChange((next ?? []).map((role) => role.id))
        }
        disabled={disabled}
      />

      {selectedItems.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBlockStart: "0.5rem",
          }}
        >
          {selectedItems.map((role) => (
            <DismissibleTag
              key={role.id}
              type="gray"
              size="md"
              text={role.name}
              onClose={() => handleRemove(role.id)}
              title={t("cta.remove")}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RolePicker;
